/**
 * The one search matcher of the app (group M, «смарт-поиск»): Ctrl+K at the table and in Подготовка, the Library,
 * «＋ Добавить материал», the link pickers, the Bestiary and the map choice all rank with it.
 *
 * - case, «ё/е» and accents do not matter; punctuation is a word break («New entry: NPC» = «new entry npc»);
 * - an item has several names (the Russian and the English name of an SRD thing, a monster's Russian alias) — the
 *   best of them counts;
 * - tiers: exact > the name starts with the query > every word of the query starts a word of the name > substring >
 *   fuzzy (a typo or two: Damerau–Levenshtein per word, the allowance grows with the word);
 * - inside a tier the caller's boost (current context, recent picks) and then a shorter name come first — a boost
 *   never lifts an item over a better tier.
 *
 * Pure functions; normalised names are cached by string, so a list that is rebuilt on every echo costs one
 * normalisation per new name, not per keystroke.
 */

export const MatchTier = {
  Fuzzy: 1,
  Substring: 2,
  WordPrefix: 3,
  Prefix: 4,
  Exact: 5,
} as const;
export type MatchTier = (typeof MatchTier)[keyof typeof MatchTier];

export interface NameMatch {
  readonly tier: MatchTier;
  /** Higher is better; comparable across items (tier dominates). */
  readonly score: number;
  /** The name that matched best (for a caption «Goblin» under «Гоблин»). */
  readonly name: string;
}

interface Prepared {
  readonly text: string;
  readonly words: readonly string[];
}

const COMBINING = /[̀-ͯ]/g;
const NON_WORD = /[^\p{L}\p{N}]+/gu;

/** «й» is a letter of its own (not «и» with a mark): kept through the accent stripping under a private-use stand-in. */
const SHORT_I = 'й';
const SHORT_I_KEEP = '';

/** Lower case, «ё»→«е» (as the GM types it either way), accents dropped except «й», punctuation and runs of space → one space. */
export function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/ё/g, 'е')
    .normalize('NFC')
    .split(SHORT_I).join(SHORT_I_KEEP)
    .normalize('NFD')
    .replace(COMBINING, '')
    .split(SHORT_I_KEEP).join(SHORT_I)
    .replace(NON_WORD, ' ')
    .trim();
}

const CACHE_LIMIT = 20_000;
const cache = new Map<string, Prepared>();

function prepare(value: string): Prepared {
  const hit = cache.get(value);
  if (hit !== undefined) return hit;
  const text = normalize(value);
  const prepared: Prepared = { text, words: text === '' ? [] : text.split(' ') };
  if (cache.size >= CACHE_LIMIT) cache.clear();
  cache.set(value, prepared);
  return prepared;
}

/** How many typos a query word of this length may carry. */
export function typoAllowance(length: number): number {
  if (length < 4) return 0;
  if (length < 7) return 1;
  return 2;
}

// Three rows reused by every call (the search runs it thousands of times per keystroke; no allocation per call).
let rowA = new Int32Array(64);
let rowB = new Int32Array(64);
let rowC = new Int32Array(64);

/** Optimal string alignment distance, or `max + 1` as soon as it is certainly above `max`. */
export function editDistance(a: string, b: string, max: number): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const n = a.length;
  const m = b.length;
  if (m + 1 > rowA.length) {
    rowA = new Int32Array(m + 1);
    rowB = new Int32Array(m + 1);
    rowC = new Int32Array(m + 1);
  }
  let prev2 = rowA;
  let prev = rowB;
  let cur = rowC;
  for (let j = 0; j <= m; j++) prev[j] = j;
  for (let i = 1; i <= n; i++) {
    cur[0] = i;
    let rowMin = i;
    const ai = a.charCodeAt(i - 1);
    for (let j = 1; j <= m; j++) {
      const bj = b.charCodeAt(j - 1);
      let v = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (ai === bj ? 0 : 1));
      if (i > 1 && j > 1 && ai === b.charCodeAt(j - 2) && a.charCodeAt(i - 2) === bj) v = Math.min(v, prev2[j - 2]! + 1);
      cur[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
    const spare = prev2;
    prev2 = prev;
    prev = cur;
    cur = spare;
  }
  return prev[m]!;
}

/** The fewest typos with which `token` is a word of the name, or the start of one; Infinity — none within allowance. */
function fuzzyWordDistance(token: string, words: readonly string[]): number {
  const max = typoAllowance(token.length);
  if (max === 0) return Number.POSITIVE_INFINITY;
  let best = Number.POSITIVE_INFINITY;
  for (const word of words) {
    const whole = editDistance(token, word, max);
    if (whole < best) best = whole;
    // A typo in a word still being typed: «фаерб» ~ «фаербол», «goblni» ~ «goblins».
    if (word.length > token.length) {
      const start = editDistance(token, word.slice(0, token.length), max);
      if (start < best) best = start;
    }
    if (best === 0) break;
  }
  return best <= max ? best : Number.POSITIVE_INFINITY;
}

const TIER_SPAN = 1000;

/** How one name answers the (already prepared) query; null — not at all. */
function matchOne(query: Prepared, name: Prepared): { tier: MatchTier; penalty: number } | null {
  const q = query.text;
  const n = name.text;
  if (q === '' || n === '') return null;
  if (n === q) return { tier: MatchTier.Exact, penalty: 0 };
  if (n.startsWith(q)) return { tier: MatchTier.Prefix, penalty: 0 };
  const tokens = query.words;
  if (tokens.every((t) => name.words.some((w) => w.startsWith(t)))) return { tier: MatchTier.WordPrefix, penalty: 0 };
  if (n.includes(q) || tokens.every((t) => n.includes(t))) return { tier: MatchTier.Substring, penalty: 0 };
  // Fuzzy: every query word is a word of the name (or its start) within its typo allowance.
  let typos = 0;
  for (const t of tokens) {
    if (name.words.some((w) => w.startsWith(t)) || n.includes(t)) continue;
    const d = fuzzyWordDistance(t, name.words);
    if (!Number.isFinite(d)) return null;
    typos += d;
  }
  return { tier: MatchTier.Fuzzy, penalty: typos * 100 };
}

/**
 * The best match of `query` among `names` (the first name is the item's own; a later one — an alias — matches
 * slightly below it at equal tier). Null when nothing matches or the query is empty.
 */
export function matchNames(query: string, names: readonly string[]): NameMatch | null {
  const q = prepare(query);
  if (q.text === '') return null;
  let best: NameMatch | null = null;
  for (let index = 0; index < names.length; index++) {
    const raw = names[index]!;
    if (raw === '') continue;
    const name = prepare(raw);
    const m = matchOne(q, name);
    if (m === null) continue;
    const score = m.tier * TIER_SPAN - m.penalty - (index === 0 ? 0 : 10) - Math.min(name.text.length, 80) * 0.5;
    if (best === null || score > best.score) best = { tier: m.tier, score, name: raw };
  }
  return best;
}

export interface Ranked<T> {
  readonly item: T;
  readonly match: NameMatch;
  readonly score: number;
}

export interface RankOptions<T> {
  /** 0…1: the current context (1) and recent picks (less) — lifts inside a tier, never over it. */
  readonly boost?: (item: T) => number;
  readonly limit?: number;
}

/** Every item that matches, best first. An empty query matches nothing — callers show their default list then. */
export function rankItems<T>(
  query: string,
  items: readonly T[],
  namesOf: (item: T) => readonly string[],
  options: RankOptions<T> = {},
): Ranked<T>[] {
  const out: Ranked<T>[] = [];
  for (const item of items) {
    const match = matchNames(query, namesOf(item));
    if (match === null) continue;
    // Boost spans up to 400 of a tier's 1000: a context item outranks a shorter name of the same tier, not a better tier.
    const boost = Math.max(0, Math.min(1, options.boost?.(item) ?? 0));
    out.push({ item, match, score: match.score + boost * 400 });
  }
  out.sort((a, b) => b.score - a.score);
  return options.limit === undefined ? out : out.slice(0, options.limit);
}

/** The items that match, best first; the list as it is when the query is empty. The one-liner of the local searches. */
export function searchFilter<T>(query: string, items: readonly T[], namesOf: (item: T) => readonly string[]): T[] {
  if (normalize(query) === '') return [...items];
  return rankItems(query, items, namesOf).map((r) => r.item);
}
